<template>
  <div>
    <peaks-player v-if="item.storage.waveform" :asset="{ ...item.storage, path: item.storage_origin_url }"
                  :markers="markers"
                  :marker-editing="true">
      <template #player_portlet_actions>
        <a :href="`/items/${item.uuid}/edit`" class="btn btn-ss">
          <i class="la la-arrow-circle-o-left" /> {{ getItemLabel(true) }}
        </a>
      </template>
    </peaks-player>
    <basic-portlet v-else title="Waveform:" icon="fa fa-signal">
      <strong>Waveform not generated</strong><br>
      <button class="btn btn-ss-standout mt-4" @click="generateWaveform">
        Generate Now
      </button>
    </basic-portlet>
    <div v-if="showFileMetaWarning">
      <div class="alert alert-elevate alert-light alert-bold fade show" role="alert">
        <div class="alert-icon">
          <i class="flaticon-warning" />
        </div>
        <div class="alert-text">
          This file already contains add tags in the files metadata. Would you like to import them ?
          <button class="ml-2 btn btn-ss-standout" @click="importAdMarkersFromFile()">
            Import Ad Markers
          </button>
        </div>
        <div class="alert-close">
          <button type="button" class="close"
                  data-dismiss="alert"
                  aria-label="Close">
            <span aria-hidden="true"><i class="la la-close" /></span>
          </button>
        </div>
      </div>
    </div>

    <basic-portlet title="Chapter Markers" icon="la la-bookmark" :show-footer="true">
      <template #portlet_actions>
        <a :href="back_url" class="btn btn-ss">
          <i class="la la-arrow-circle-o-left" /> Back to {{ getItemLabel(true) }}
        </a>
      </template>

      <ul v-if="markers.length" class="list-group">
        <li v-for="(marker, index) in markers" :key="index" class="row list-group-item">
          <div class="col-12 col-md-2 float-left">
            <label>Marker Time *</label>
            <input v-model="marker.time_formatted"
                   v-validate="'is_time|required'"
                   v-mask="'##:##:##'"
                   data-vv-as="Marker Time"
                   class="form-control kt-input"
                   type="text" :name="'time-'+index"
                   placeholder="00:00:00" @change="markerTimeChanged(marker)">
            <span v-show="errors.has('time-'+index)" class="text-ss-danger"><br>{{ errors.first(`time-${index}`) }}</span>
          </div>
          <div class="col-12 col-md-3 float-left">
            <div v-if="!marker.is_ad">
              <label>Name *</label>
              <input :value="marker.name"
                     class="form-control kt-input"
                     type="text" :name="'name-'+index"
                     placeholder="Name your chapter" @input="(ev) => markerNameChanged(marker, ev.target.value)">
              <span v-show="errors.has('name-'+index)" class="text-ss-danger"><br>{{ errors.first(`name-${index}`) }}</span>
            </div>
          </div>
          <div class="col-12 col-md-3 float-left">
            <div v-if="!marker.is_ad">
              <label>Description</label>
              <textarea v-model="marker.description" class="form-control kt-input"
                        :name="'description-'+index"
                        placeholder="Describe your chapter" />
            </div>
          </div>
          <div class="col-12 col-md-2 float-left">
            <div class="d-flex flex-column">
              <ss-checkbox :id="`marker-${index}-enabled`" v-model="marker.enabled">
                Enabled
              </ss-checkbox>
              <ss-checkbox v-if="customer_data.ads_enabled"
                           :id="`marker-${index}-ads-enabled`"
                           v-model="marker.is_ad"
                           @change="markerChanged">
                Is Ad ?
              </ss-checkbox>
              <ss-checkbox :id="`marker-${index}-sticky`" v-model="marker.sticky" title="Sticky chapters appear at the top and are sorted by time.">
                Sticky ?
              </ss-checkbox>
            </div>
          </div>

          <div class="col-12 col-md-2 hidden-sm float-left text-right">
            <button type="button"
                    class="btn btn-ss btn-icon"
                    data-toggle="kt-tooltip"
                    data-placement="top"
                    title="Play from Marker"
                    @click="seekToMarker(marker)">
              <i class="fa fa-play" />
            </button>

            <button type="button"
                    class="btn btn-ss-danger btn-icon"
                    @click="() => removeMarker(marker)">
              <i class="fa fa-trash" />
            </button>
          </div>

          <div class="col-12 hidden-md-up float-left">
            <a href="javascript:void(0)" class="btn btn-ss kt-btn" @click="seekToMarker(marker)">Play From Marker</a>
            <a href="javascript:void(0)" class="btn btn-ss-danger kt-btn" @click="() => removeMarker(marker)"><i class="la la-trash" />Remove</a>
          </div>
        </li>
      </ul>

      <p v-else>
        <strong>This {{ getItemLabel() }} currently has no markers.</strong>
      </p>

      <template #footer>
        <div class="row">
          <div class="col-12">
            <button id="save_markers_btn" style="float: right;"
                    class="btn btn-ss-standout"
                    @click="saveMarkers">
              Save
            </button>
            <button id="preview_markers" style="float: right;"
                    class="btn btn-ss mr-2"
                    @click="showPreview()">
              <span v-if="previewing">Refresh Preview</span>
              <span v-else>Preview</span>
            </button>
          </div>
        </div>
      </template>
    </basic-portlet>

    <div v-if="previewing" class="kt-portlet">
      <div class="kt-portlet__head">
        <div class="kt-portlet__head-label">
          <span class="kt-portlet__head-icon">
            <i class="la la-eye" />
          </span>
          <h3 class="kt-portlet__head-title">
            Preview
          </h3>
        </div>
      </div>
      <div class="kt-portlet__body">
        <player-iframe-preview :item="item" :show-tools="true" />
      </div>
    </div>
    <quick-alert-modal v-bind="quick_alert" />
    <waveform-generating-modal />
  </div>
</template>

<script>
import BasicPortlet from 'VueComponents/BasicPortlet';
import PeaksPlayer from "VuePartials/App/Chapters/PeaksPlayer";
import VueTheMask from "vue-the-mask";
import VeeValidate from 'vee-validate';
import Ladda from "ladda";
import "ladda/dist/ladda-themeless.min.css";
import PlayerIframePreview from "AssetsJs/components/PlayerIframePreview";
import WaveformGeneratingModal from "VueModals/WaveformGeneratingModal";

Vue.use(VueTheMask);
Vue.use(VeeValidate);

import { convertToHhmmss } from "Helpers/formatters";

export default {
  components: {
    BasicPortlet,
    PeaksPlayer,
    PlayerIframePreview,
    WaveformGeneratingModal,
  },
  data: () => ({
    item: window.item,
    markers: [],
    previewing: false,
    customer_data: window.user.customer,
  }),
  computed: {
    back_url() {
      const part = this.getItemLabel() === 'episode' ? 'episode' : 'items'; // item path is plural
      return `/${part}/${this.item.uuid}/edit`;
    },
    showFileMetaWarning() {
      const has_comments = !this.markers.length
        && this.item.storage
        && this.item.storage.ffmpeg_details
        && this.item.storage.ffmpeg_details.tags
        && this.item.storage.ffmpeg_details.tags.comment;

      if (!has_comments) {
        return false;
      }
      return this.item.storage.ffmpeg_details.tags.comment.includes("ADWTAG_");
    },
    markersSorted() {
      let markers = JSON.parse(JSON.stringify(this.markers));

      if (!this.customer_data.ads_enabled) {
        markers = _.filter(markers, marker => !marker.is_ad);
      }

      return _.sortBy(markers, ['time']);
    },
    hasChanges() {
      const vm = this;

      if (!this.markers || !this.item.chapter_markers) {
        return true;
      }

      if (this.markers.length !== this.item.chapter_markers.length) {
        return true;
      }

      let equal = true;
      _.forEach(this.markers, (marker, index) => {
        if (equal) {
          equal = _.isEqual(marker, vm.item.chapter_markers[index]);
        }
      });

      return !equal;
    },
  },
  beforeMount() {
    this.updateMarkers();
  },
  mounted() {
    const vm = this;

    EventBus.$on('marker-time-changed', (payload) => {
      vm.markers[payload.marker_index].time = payload.time;
      vm.markers[payload.marker_index].time_formatted = convertToHhmmss(payload.time);
    });

    EventBus.$on('loaded', () => {
      $('.loader').each(function () {
        $(this).removeClass("visible");
        $(this).hide();
      });
    });

    EventBus.$on('add-marker', (payload) => {
      const marker = {
        time: Math.round(payload.current_time),
        time_formatted: payload.current_time_formatted,
        name: null,
        description: null,
        is_ad: 0,
        enabled: 1,
      };

      if (this.chapter) {
        marker.chapter_id = this.chapter.uuid;
      }
      this.markers.push(marker);
      this.changed = true;
    });

    window.EventBus.$on(`peaks-marker`, (payload) => {
      const add_marker = (marker) => {
        this.markers.push({
          ...marker,
          chapter_id: this.chapter?.uuid,
        });
        this.sortMarkers();
      };
      const old_marker_index = this.markers.findIndex(marker => marker.time === payload.old?.time);
      switch (payload.action) {
        case `added`:
          add_marker(payload.marker);
          break;
        case `dragged`:
          this.markers.splice(old_marker_index, 1); // kill existing marker
          add_marker(payload.new);
          break;
      }
      if (!this.nullish(payload.action)) this.changed = true;
    });
  },
  created() {
    VeeValidate.Validator.extend("is_time", {
      getMessage: () => `The format must be HH:MM:SS`,
      validate: value =>
        new Promise((resolve) => {
          const regex = new RegExp(
            "([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])",
          );
          resolve({
            valid: value && regex.test(value),
          });
        }),
    });
  },

  methods: {
    sortMarkers() {
      let markers = JSON.parse(JSON.stringify(this.markers));

      if (!this.customer_data.ads_enabled) {
        markers = _.filter(markers, marker => !marker.is_ad);
      }

      const sorted = _.sortBy(markers, ['time']);

      this.markers = sorted;
    },
    async generateWaveform() {
      try {
        // await axios.post(`/api/v1/storage/${this.item.storage.uuid}/generate-waveform`);
        this.fireModal('waveform_generating');
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
    importAdMarkersFromFile() {
      axios.post(`/api/v1/chapter-markers/${this.item.uuid}/import-ad-markers`).then((response) => {
        this.item = response.data.data;
        this.updateMarkers();
        toastr.success("Ad Markers Imported.");
      }).catch((error) => {
        window.handleAxiosError(error);
      });
    },
    showPreview() {
      if (this.hasChanges) {
        toastr.error("Please save your changes");
        return;
      }
      this.previewing = true;
    },
    seekToMarker(marker) {
      EventBus.$emit(`peaks-cmd`, {
        cmd: `play`,
        args: {
          time: marker.time,
        },
      });
    },
    removeMarker(marker) {
      this.changed = true;
      window.EventBus.$emit(`peaks-remove`, {
        type: `point`,
        attrs: marker,
      });
      this.markers.splice(this.markers.findIndex(mrk => mrk.time === marker.time), 1);
    },
    markerTimeChanged(marker) {
      // Set the 'time' value based on the formatted hh:mm:ss
      const old_time = marker.time;
      const a = marker.time_formatted.split(':');
      const seconds = (+a[0]) * 60 * 60 + (+a[1]) * 60 + (+a[2]);
      marker.time = seconds;
      this.markers.splice(this.markers.findIndex(m => m.time === marker.time), 1);
      window.EventBus.$emit(`peaks-update-time`, {
        marker: marker,
        time: old_time,
      });
    },
    markerNameChanged(marker, name) {
      window.EventBus.$emit(`peaks-update-name`, {
        marker: marker,
        name: name,
      });
      marker.name = name;
    },
    markerChanged() {
      // this.sortMarkers(); // removed this as it makes the list update while editing, can be v annoying with lots of markers
    },
    saveMarkers() {
      const ladda_btn = Ladda.create(document.querySelector("#save_markers_btn"));

      ladda_btn.start();

      this.$validator.validateAll().then((result) => {
        if (result) {
          const data = {
            markers: this.markers,
          };

          axios.post(`/api/v1/chapter-markers/${window.item.uuid}`, data).then((response) => {
            if (response.data && response.data.data) {
              this.item = response.data.data;
              // Create non-reactive clone of marker data
              this.updateMarkers();
              toastr.success("Markers Saved");
              this.changed = false;
            }
            ladda_btn.stop();

            if (this.previewing) {
              // re-render the preview
              this.previewing = false;
              this.$nextTick(() => {
                this.previewing = true;
              });
            }
          }).catch((error) => {
            window.handleAxiosError(error);
            ladda_btn.stop();
          });
        } else {
          ladda_btn.stop();
          toastr.error("Some fields have not been populated correctly.");
        }
      });
    },
    updateMarkers() {
      const vm = this;
      vm.markers = [];
      this.item.chapter_markers.forEach((marker) => {
        marker.time_formatted = convertToHhmmss(marker.time);
        // Create a non-reactive copy of the marker
        const marker_clone = JSON.parse(JSON.stringify(marker));
        vm.markers.push(marker_clone);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
    .list-group-item {
        display: -ms-flexbox!important;
        display: flex!important;
        -ms-flex-align: center!important;
        align-items: center!important;
    }

    .chapter_data {
        margin-bottom:20px;
    }

    span.save-tooltip {
        float:right;
        display:block;
        cursor:pointer;
        &.disabled {
            cursor:not-allowed;
        }
    }

    .disabled {
        cursor:not-allowed;
    }

    .loader {
        position:absolute;
        z-index:10;
        top:0;
        height:100%;
        width:100%;
        text-align:center;
        background: rgba(0, 0, 0, 0.3);
        display: flex;
        justify-content: center;
        align-items: center;
    }
</style>
